import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as Popover from '@radix-ui/react-popover';
import { FileContext } from '../../../context/FileContext';
import RequiredIcon from '../../../assets/icons/astrik.svg';
import CalendarIcon from '../../../assets/icons/datepicker.svg';
import { DatePicker } from '@vibe/core/next';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import DialogContentContainer from 'monday-ui-react-core/dist/DialogContentContainer';
import moment from 'moment';

const OuterContainer = styled.div`
  position: absolute;
  top: ${({ y }) => y + 'px'};
  left: ${({ x }) => x + 'px'};
  width: ${({ width }) => width + 'px'};
  height: ${({ height }) => height + 'px'};
  pointer-events: auto;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  margin-left: auto;

  img.required {
    position: absolute;
    top: 4px;
    right: 4px;
  }

  &.date-picker {
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const DatePickerContainer = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #c5c7d0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 2px 10px;
  color: #676879;
  border: ${({ required }) =>
    `${required ? '1px solid #D83A52' : '1px solid #c5c7d0'}`};
  background: ${({ color }) => (color ? color : 'white')};
  cursor: pointer;
  img {
    margin: 0 0 0 auto;
  }
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  font-size: ${({ fontSize }) => fontSize + 'px'};
`;

const CalendarWrapper = styled.div`
  z-index: 999999;
  pointer-events: auto;
  table {
    border: none !important;
  }

  th,
  .th,
  td,
  .td {
    box-shadow: none;
  }

  .CalendarDay__default {
    border-top: none;
    border-bottom: none;
    width: 100%;
  }
  .CalendarDay {
    font-size: 12px;
    line-height: 30px;
  }
`;

const MIN_WIDTH = 50;
const MIN_HEIGHT = 15;

const Img = styled.img`
  position: absolute;

  height: ${({ height }) =>
    height > MIN_HEIGHT ? MIN_HEIGHT + 'px' : height + 'px'};
  top: '4px';
  left: ${({ height, width, fontSize }) =>
    height < 20 || width < MIN_WIDTH + 10 ? '2px' : width - 30 + 'px'};
  font-size: ${({ fontSize }) => fontSize + 'px'};
  width: fit-content;
`;

const PreviewImg = styled.img`
  height: ${({ height }) =>
    height > MIN_HEIGHT ? MIN_HEIGHT + 'px' : height + 'px'};
  top: '4px';
  left: ${({ height, width, fontSize }) =>
    height < 20 || width < MIN_WIDTH + 10 ? '2px' : width - 30 + 'px'};
  font-size: ${({ fontSize }) => fontSize + 'px'};
  width: fit-content;
`;

const DatePicked = styled.div`
  font-size: ${({ fontSize }) => fontSize + 'px'};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  width: max-content;
  &:hover {
    border-color: #c5c7d0;
    color: #333;
  }
  &.hover-active:hover {
    border: 1px solid rgba(0, 115, 234, 0.4) !important;
    border-radius: 4px !important;
  }
`;

const PreviewIconContainers = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  column-gap: 4px;
`;

const DatePickerPreview = ({ x, y, item, from }) => {
  const padding = 6; //this is added because the x and y coordinates are being calculated from initial point including border
  const fontSize = item.fontSize > 10 ? item.fontSize : 11;

  const { rgbMapper } = useColumnOrder();
  const itemColor = item.color ?? item.signer?.color;
  const backgroundColor = itemColor
    ? rgbMapper[itemColor] ?? itemColor
    : '#E6E6FA';
  const { addedFormFields, updateFormFields } = useContext(FileContext);
  const [datePicked, setDatePicked] = useState(
    item?.content ? moment(item.content).toDate() : undefined,
  );
  const [showPicker, setShowPicker] = useState(false);
  const [isRequired, setIsRequired] = useState(false);

  const handleInput = (date) => {
    if (!date) return;

    setDatePicked(date);
    setShowPicker(false);
    item.content && setIsRequired(false);
    if (from !== '/sign') return;

    const selectedItem = addedFormFields.find((x) => x.id === item.id);
    const pickedDate = moment(date).format(item?.format?.format);
    selectedItem.content = pickedDate;
    setIsRequired(false);
    selectedItem.hasError = false;
    updateFormFields([...addedFormFields]);
  };

  useEffect(() => {
    if (item.hasError) {
      setIsRequired(true);
    }
  }, [addedFormFields, item.hasError]);

  return (
    <Popover.Root open={showPicker} onOpenChange={setShowPicker}>
      <OuterContainer
        x={x + padding}
        y={y + padding}
        height={item.height}
        width={item.width}
      >
        <Popover.Trigger asChild>
          <div style={{ width: '100%', height: '100%', cursor: 'pointer' }}>
            {datePicked ? (
              <DatePicked
                fontSize={fontSize}
                className={item.isActive ? 'active' : 'hover-active'}
              >
                <p>
                  {moment(datePicked).format(
                    item?.format?.format || 'DD/MM/YYYY',
                  )}
                </p>

                <PreviewIconContainers>
                  <PreviewImg
                    className="date-icon"
                    src={CalendarIcon}
                    alt="date picker"
                    height={item.height}
                    width={item.width}
                    fontSize={
                      fontSize < 15
                        ? 15
                        : fontSize > item.height && item.height < 24
                        ? item.height - 4
                        : fontSize > 20
                        ? 20
                        : 20
                    }
                  />
                  {item.required ? (
                    <img
                      className="required"
                      src={RequiredIcon}
                      alt=""
                      width={10}
                    />
                  ) : null}
                </PreviewIconContainers>
              </DatePicked>
            ) : (
              <DatePickerContainer
                size={DialogContentContainer.sizes.SMALL}
                color={backgroundColor}
                required={isRequired}
                fontSize={fontSize}
              >
                {item.width > MIN_WIDTH + 40 && item.height > MIN_HEIGHT + 4 ? (
                  <p>--Pick a date--</p>
                ) : null}
                <IconContainer>
                  <Img
                    className="date-icon"
                    src={CalendarIcon}
                    alt="date picker"
                    height={item.height}
                    width={item.width}
                    fontSize={
                      fontSize < 15
                        ? 15
                        : fontSize > item.height && item.height < 24
                        ? item.height - 4
                        : fontSize > 20
                        ? 20
                        : 20
                    }
                  />
                  {item.required ? (
                    <img
                      className="required"
                      src={RequiredIcon}
                      alt=""
                      width={10}
                    />
                  ) : null}
                </IconContainer>
              </DatePickerContainer>
            )}
          </div>
        </Popover.Trigger>
      </OuterContainer>

      <Popover.Portal>
        <Popover.Content
          sideOffset={10}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          asChild
        >
          <CalendarWrapper>
            <DialogContentContainer className="DatePicker-stories-module_datepickerDialogContentContainer">
              <DatePicker
                data-testid="date-picker"
                date={datePicked}
                onDateChange={handleInput}
              />
            </DialogContentContainer>
          </CalendarWrapper>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default DatePickerPreview;
